import {makeAutoObservable} from "mobx"
import {merge, pick} from "lodash-es"
import {load, TLoadTrack, TUserTrack} from "@ui/api/track"
import routeService from "@ui/services/RouteService"
import mapService from "@ui/services/MapService"
import {Filter, IFilter, TListState} from "../common/filter"
import store from "../"

type TSearchParams = {
	name?: string
	favorite?: true
  color?: number | null
}

export interface IRoute {
	readonly tracks: TUserTrack[] | undefined
  readonly filter: IFilter<TSearchParams>
  readonly state: TListState
  readonly empty: boolean | undefined

  setEmpty(empty: boolean): void
	isDisplay(routeId?: number): boolean
	toggle(routeId: number): void
	show(routeId: number): Promise<void>
  delete(routeId: number): void
  update(routeId: number, track: Partial<TUserTrack>): void
	hide(routeId: number): void
	hideAll(): void
	to(routeId: number): void
	appendSearch(tracks: TUserTrack[]): void
	clear(): void
  changeState(props: Partial<TListState>): void
}

export class Route implements IRoute {
	cacheSize: number = 100
	features = new Map<number, TLoadTrack>()
  display: number[] = []
	tracks: TUserTrack[] = []
	filter: IFilter<TSearchParams> = new Filter({color: null})
  state: TListState = {}
  empty: boolean | undefined

  constructor() {
    makeAutoObservable(this)
	}

  setEmpty(empty: boolean) {
    this.empty = empty
  }

	async show(routeId: number): Promise<void> {
		let route = this.features.get(routeId)
		if (!route) {
			await load(routeId).then((feature) => {
          const featureId = Number(feature.props.id)
          if (featureId && featureId == routeId) {
            this.features.set(featureId, feature)
            route = feature
            if (this.features.size > this.cacheSize) {
              this.features.delete(this.features.keys().next().value)
            }
          }
        }
      )
		}
		if (route) {
			const {movement} = store
			routeService.addRoute(route)
			this.to(routeId)
			movement.isFollowing && movement.switchFollowing()
		}
	}

	delete(routeId: number): void {
		this.features.delete(routeId)
    this.tracks = this.tracks?.filter(track => track.id !== routeId)
    this.hide(routeId)
	}

  update(routeId: number, data: Partial<TUserTrack>): void {
    this.tracks = this.tracks?.map(track => track.id == routeId ? {...track, ...data} : track)
    const feature = this.features.get(routeId)
    if (feature) this.features.set(routeId, {...feature, props: {...feature.props, ...data}})
    routeService.updateRouteProps(routeId, pick(data, ["color", "name", "favorite"]))
  }

	to(routeId: number): void {
		const extent = routeService.getTrackExtent(routeId)
		extent && mapService.moveToExtent(extent)
	}

	toggle(routeId: number): void {
    if (this.display.includes(routeId)) {
      this.display = this.display.filter(id => id !== routeId)
    } else {
      this.display = [...this.display, routeId]
    }
	}

	hide(routeId: number): void {
		routeService.deleteRoute(routeId)
    this.display = this.display.filter(id => id !== routeId)
	}

	hideAll(): void {
    [...this.display].forEach(this.hide.bind(this))
	}

	isDisplay(routeId?: number): boolean {
		return routeId ? this.display.includes(routeId) : !!this.display.length
	}

	appendSearch(tracks: TUserTrack[]): void {
		this.tracks = [...(this.tracks || []), ...tracks]
	}

	clear(): void {
		this.tracks = []
    this.state = {}
	}

  changeState(props: Partial<TListState>) {
    this.state = merge(this.state, props)
  }
}
